// Copyright (c) Bentley Systems
import {
  ActionColumn,
  ComboBox,
  DefaultCell,
  DropdownMenu,
  IconButton,
  InformationPanel,
  InformationPanelBody,
  InformationPanelContent,
  InformationPanelHeader,
  InformationPanelWrapper,
  MenuItem,
  ProgressRadial,
  Table,
  Text,
  Textarea,
} from "@itwin/itwinui-react";
import {
  CellProps,
  CellRendererProps,
  Column,
  Row,
} from "@itwin/itwinui-react/react-table";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ConfigManager } from "../Helpers/config-manager";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { selectAccessToken } from "../app/authorization-slice";
import {
  customCellStringSort,
  FormatDate,
  setErrorState,
} from "../Helpers/helper-functions";
import { mapDataToModels } from "../Helpers/map-response";
import {
  SvgManager,
  SvgMore,
  SvgTimer,
} from "@itwin/itwinui-icons-react";
import { appendLoadMoreOption, LOAD_MORE_VALUE, paginatedItemRenderer, searchPlaceholder } from "../Helpers/ComboBoxHelpers";
import ManageAccess from "./ManageAccessRolesAssignmentForm";
import ProjectDebugLoggingForm from "./ProjectDebugLoggingForm";
import IconByName from "../Icons/IconByName";
import {
  selectMainMenuHeightWithMargin,
  selectSideMenuWidthWithMargin,
} from "../app/header-slice";
import { DawComponent } from "../Helpers/daw-component";
import { useITwinProjects } from "../Helpers/useITwinProjects";
import { ITwin } from "./Models/itwin";
import { ProjectSubscription } from "./Models/project-subscription";
import { useNodePermission } from "../Helpers/useNodePermission";
import { useODataTableState } from "../Helpers/useODataTableState";
import { useElementHeights } from "../Helpers/useElementHeights";
import { ODataTextFilter } from "../Helpers/ODataTextFilter";
import { choiceFilter } from "../Helpers/TableChoiceFilter";
import { useITwinDebugLogging } from "./useProjectDebugLogging";
import {
  DEBUG_LOGGING_FILTER_OPTIONS,
  debugLoggingODataFilter,
  describeDebugLogging,
  ProjectDebugLogging,
} from "./Models/project-debug-logging";

const MANAGE_ACCESS_FROLL = "package";
const DEBUG_LOGGING_FROLL = "debugLogging";
const DEBUG_LOGGING_COLUMN_ID = "debugLogging";
const DebugLoggingFilter = choiceFilter(DEBUG_LOGGING_FILTER_OPTIONS, "Debug logging");

type SubscribedProjectTableRow = {
  name: string;
  uniqueName: string;
  version: string;
  isOdata: boolean;
  projectStageId: string;
  description: string;
  subscribedOn: string;
  subscribedByName: string;
  subscription: ProjectSubscription;
  debugLogging: ProjectDebugLogging | undefined;
};

const ExpandedProjectDetail = ({
  projectId,
  accessToken,
}: {
  projectId: string;
  accessToken: string;
}) => {
  const [inputExample, setInputExample] = useState<string>();
  const [responseExample, setResponseExample] = useState<string>();
  const [isLoading, setIsLoading] = useState(true);
  const dispatch = useAppDispatch();

  useEffect(() => {
    const abortController = new AbortController();
    const fetchDetail = async () => {
      const runId = crypto.randomUUID();
      try {
        const url =
          `${ConfigManager.projectProviderUrl}/odata/Projects(${projectId})?$select=Id&$expand=InputInfo`;
        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: accessToken,
            RunId: runId,
          },
          signal: abortController.signal,
        });
        if (!response.ok) {
          setErrorState(response.status, dispatch, runId, false);
          return;
        }
        const data = await response.json();
        if (data?.InputInfo) {
          setInputExample(
            data.InputInfo.InputExample
              ? atob(data.InputInfo.InputExample)
              : undefined,
          );
          setResponseExample(
            data.InputInfo.ResponseExample
              ? atob(data.InputInfo.ResponseExample)
              : undefined,
          );
        }
      } catch (error: any) {
        if (error?.name !== "AbortError") {
          setErrorState(500, dispatch, runId, true);
        }
      } finally {
        setIsLoading(false);
      }
    };
    void fetchDetail();
    return () => {
      abortController.abort();
    };
  }, [projectId, accessToken, dispatch]);

  if (isLoading) {
    return <ProgressRadial indeterminate size="small" />;
  }

  return (
    <>
      <div
        className="becs-t-margin"
        style={{ display: "flex", marginBottom: "8px", alignItems: "flex-start" }}
      >
        <Text variant="body" style={{ flex: "0 0 150px", paddingTop: 4 }}>
          Input example:
        </Text>
        <Textarea
          readOnly
          value={inputExample ?? ""}
          style={{ flex: "1", resize: "vertical", height: 80, fontFamily: "monospace", fontSize: 12 }}
        />
      </div>
      <div
        className="becs-t-margin"
        style={{ display: "flex", marginBottom: "8px", alignItems: "flex-start" }}
      >
        <Text variant="body" style={{ flex: "0 0 150px", paddingTop: 4 }}>
          Response example:
        </Text>
        <Textarea
          readOnly
          value={responseExample ?? ""}
          style={{ flex: "1", resize: "vertical", height: 80, fontFamily: "monospace", fontSize: 12 }}
        />
      </div>
    </>
  );
};

const SubscribedProjectList = () => {
  const [fRollUrl, setFrollUrl] = useState<string>();
  const { isNodeAllowed } = useNodePermission(
    DawComponent.DAW_COMPONENT_SUBSCRIBED_PROJECTS,
  );
  // The page is open to anyone who manages packages, but Debug logging is for architects only.
  // Project management (DAW_COMPONENT_GENERAL_PROJECTS) needs the same architect permission
  // (EDF_ARCHITECT_DevelopProject) that ProjectProvider checks, so it decides who sees the feature.
  // ProjectProvider still has the final word: it also requires membership of the iTwin.
  const { isNodeAllowed: isArchitect } = useNodePermission(
    DawComponent.DAW_COMPONENT_GENERAL_PROJECTS,
    { enabled: isNodeAllowed, silent: true },
  );
  const sideMenuWidth = useAppSelector(selectSideMenuWidthWithMargin);
  const mainMenuHeight = useAppSelector(selectMainMenuHeightWithMargin);
  const headerDivRef = useRef<HTMLDivElement>(null);
  const headerButtonsDivRef = useRef<HTMLDivElement>(null);
  const [headerDivHeight, headerButtonsDivHeight] = useElementHeights(
    headerDivRef,
    headerButtonsDivRef,
  );
  const accessToken = useAppSelector(selectAccessToken);
  const [subscribedProjects, setSubscribedProjects] = useState<
    ProjectSubscription[]
  >([]);
  const [selectedPackage, setSelectedPackage] =
    useState<ProjectSubscription | null>(null);
  const dispatch = useAppDispatch();

  // Use the iTwin projects hook
  const {
    projects,
    selectedProject,
    isProjectsLoading,
    nextLink,
    fetchNextProjects,
    handleSearchChange,
    handleProjectSelect,
    selectOptions,
    emptyContent,
  } = useITwinProjects({
    isEnabled: isNodeAllowed,
    accessToken,
    dispatch,
  });

  const {
    pageSize,
    sortBy,
    isLoading,
    filters,
    tableFiltersState,
    setPageIndex,
    setTotalCount,
    setIsLoading,
    handleSort,
    handleFilter,
    paginator,
    buildOrderBy,
    buildFilterStr,
    skip,
    top,
  } = useODataTableState();

  const [actionCount, setActionCount] = useState<number>(0);

  const { debugLoggingByProject, isDebugLoggingAvailable: isDebugLoggingLoaded } =
    useITwinDebugLogging({
      iTwinId: selectedProject?.id,
      accessToken,
      dispatch,
      isEnabled: isNodeAllowed && isArchitect,
      refreshKey: actionCount,
    });
  const isDebugLoggingAvailable = isArchitect && isDebugLoggingLoaded;

  // The Debug logging filter lists the projects that have it on, so it follows the loaded grants.
  // It is ignored while the column is hidden.
  const debugLoggingFilterValue = filters.find(
    (f) => f.id === DEBUG_LOGGING_COLUMN_ID,
  )?.value;
  const debugLoggingNames = useMemo(
    () => [...debugLoggingByProject.keys()].sort(),
    [debugLoggingByProject],
  );
  const debugLoggingClause = useMemo(
    () =>
      isDebugLoggingAvailable
        ? debugLoggingODataFilter(
            debugLoggingFilterValue,
            debugLoggingNames,
            "Project/UniqueName",
          )
        : "",
    [isDebugLoggingAvailable, debugLoggingFilterValue, debugLoggingNames],
  );

  const openDebugLogging = useCallback((subscription: ProjectSubscription) => {
    setSelectedPackage(subscription);
    setFrollUrl(DEBUG_LOGGING_FROLL);
  }, []);

  // --- Table: action column with "Manage access" and "Debug logging" ---
  const buttonCellTemplate = useCallback(
    (cellProps: CellProps<any>) => {
      const menuItems = (close: () => void) => [
        <MenuItem
          key="miManageAccess"
          startIcon={<SvgManager />}
          onClick={() => {
            close();
            setSelectedPackage(cellProps.row.original.subscription);
            setFrollUrl(MANAGE_ACCESS_FROLL);
          }}
        >
          Manage access
        </MenuItem>,
        ...(isDebugLoggingAvailable
          ? [
              <MenuItem
                key="miDebugLogging"
                startIcon={<SvgTimer />}
                onClick={() => {
                  close();
                  openDebugLogging(cellProps.row.original.subscription);
                }}
              >
                Debug logging
              </MenuItem>,
            ]
          : []),
      ];

      return (
        <>
          <DropdownMenu menuItems={menuItems}>
            <IconButton
              styleType="borderless"
              onClick={(e) => e.stopPropagation()}
              label="Actions"
            >
              <SvgMore />
            </IconButton>
          </DropdownMenu>
          <div className="becs-table-cell-shadow-left-elist" />
        </>
      );
    },
    [setFrollUrl, setSelectedPackage, isDebugLoggingAvailable, openDebugLogging],
  );

  // --- Table columns ---
  const columns = useMemo<Column<SubscribedProjectTableRow>[]>(() => {
    return [
      {
        Header: "Name",
        accessor: "name",
        id: "name",
        minWidth: 350,
        Filter: ODataTextFilter,
      },
      {
        Header: "Version",
        accessor: "version",
        id: "version",
        minWidth: 140,
        Filter: ODataTextFilter,
      },
      {
        Header: "Subscribed on",
        accessor: "subscribedOn",
        id: "subscribedOn",
        minWidth: 200,
        cellRenderer: (
          props: CellRendererProps<SubscribedProjectTableRow>,
        ) => {
          return (
            <DefaultCell {...props}>
              {FormatDate(props.cellProps.value)}
            </DefaultCell>
          );
        },
      },
      {
        Header: "Odata",
        accessor: "isOdata",
        sortType: customCellStringSort,
        id: "isOdata",
        width: 100,
        disableToggleVisibility: false,
        cellRenderer: (props: CellRendererProps<SubscribedProjectTableRow>) => {
          return (
            <DefaultCell
              {...props}
              style={{ alignItems: "center", justifyContent: "center" }}
            >
              {props.cellProps.value === true ? (
                <IconByName name="StatusSuccessColor" />
              ) : (
                <IconByName name="StatusSuccessColorMuted" />
              )}
            </DefaultCell>
          );
        },
      },
      {
        Header: "Subscribed by",
        accessor: "subscribedByName",
        id: "subscribedByName",
        minWidth: 200,
        Filter: ODataTextFilter,
      },
      ...(isDebugLoggingAvailable
        ? [
            {
              Header: "Debug logging",
              accessor: "debugLogging",
              id: DEBUG_LOGGING_COLUMN_ID,
              width: 170,
              disableSortBy: true,
              Filter: DebugLoggingFilter,
              cellRenderer: (
                props: CellRendererProps<SubscribedProjectTableRow>,
              ) => {
                const row = props.cellProps.row.original;
                return (
                  <DefaultCell
                    {...props}
                    style={{ alignItems: "center", justifyContent: "center" }}
                  >
                    {row.debugLogging ? (
                      <IconButton
                        styleType="borderless"
                        size="small"
                        label={describeDebugLogging(row.debugLogging)}
                        onClick={(e) => {
                          e.stopPropagation();
                          openDebugLogging(row.subscription);
                        }}
                      >
                        <IconByName name="StatusSuccessColor" />
                      </IconButton>
                    ) : (
                      ""
                    )}
                  </DefaultCell>
                );
              },
            } as Column<SubscribedProjectTableRow>,
          ]
        : []),
      {
        ...ActionColumn({ columnManager: true }),
        Cell: buttonCellTemplate,
        sticky: "right",
      },
    ];
  }, [buttonCellTemplate, isDebugLoggingAvailable, openDebugLogging]);

  // --- Table row data ---
  const tableData = useMemo<SubscribedProjectTableRow[]>(() => {
    return subscribedProjects.map((sub) => ({
      name: sub.Project.Name,
      uniqueName: sub.Project.UniqueName,
      version: sub.Project.Version,
      isOdata: sub.Project.IsOdata,
      projectStageId: sub.Project.ProjectStageId,
      description: sub.Project.Description,
      subscribedOn: sub.SubscribedOn,
      subscribedByName: sub.SubscribedByName,
      subscription: sub,
      debugLogging: debugLoggingByProject.get(sub.Project.UniqueName),
    }));
  }, [subscribedProjects, debugLoggingByProject]);

  // --- Expanded sub-component ---
  const expandedSubComponent = useCallback(
    (row: Row<SubscribedProjectTableRow>) => {
      const proj = row.original;
      return (
        <div style={{ width: "100%" }}>
          <div
            className="becs-all-margin"
            style={{ display: "flex", flexDirection: "column" }}
          >
            <div style={{ display: "flex", marginBottom: "8px" }}>
              <Text variant="body" style={{ flex: "0 0 150px" }}>
                Unique name:
              </Text>
              <Text variant="body" style={{ flex: "1" }}>
                <b>{proj.uniqueName}</b>
              </Text>
            </div>
            <div
              className="becs-t-margin"
              style={{ display: "flex", marginBottom: "8px" }}
            >
              <Text variant="body" style={{ flex: "0 0 150px" }}>
                Project stage:
              </Text>
              <Text variant="body" style={{ flex: "1" }}>
                <b>{proj.projectStageId}</b>
              </Text>
            </div>
            <div
              className="becs-t-margin"
              style={{ display: "flex", marginBottom: "8px" }}
            >
              <Text variant="body" style={{ flex: "0 0 150px" }}>
                Description:
              </Text>
              <Text variant="body" style={{ flex: "1" }}>
                <b>{proj.description}</b>
              </Text>
            </div>
            <ExpandedProjectDetail
              projectId={proj.subscription.ProjectId}
              accessToken={accessToken}
            />
          </div>
        </div>
      );
    },
    [accessToken],
  );

  // --- Fetch subscribed projects via OData ---
  // Map table column IDs to OData field paths
  const columnToODataPath: Record<string, string> = useMemo(
    () => ({
      name: "Project/Name",
      version: "Project/Version",
      subscribedOn: "SubscribedOn",
      subscribedByName: "SubscribedByName",
    }),
    [],
  );

  const fetchSubscribedProjects = useCallback(
    async (iTwinId: string, signal?: AbortSignal) => {
      setSubscribedProjects([]);

      if (debugLoggingClause === null) {
        // Filtered to Debug logging on, but no project has it on.
        setTotalCount(0);
        return;
      }

      const orderby = buildOrderBy(columnToODataPath);
      const filterStr = buildFilterStr(columnToODataPath, {
        [DEBUG_LOGGING_COLUMN_ID]: debugLoggingClause,
      });

      const url =
        `${ConfigManager.projectProviderUrl}/odata/Subscriptions?iTwinId=${iTwinId}` +
        `&$count=true&$expand=Project($select=Id,Name,UniqueName,Version,IsOdata,ProjectStageId,Description)` +
        `&$select=ProjectId,SubscribedOn,SubscribedByName&$skip=${skip}&$top=${top}${orderby}${filterStr}`;

      const runId = crypto.randomUUID();
      setIsLoading(true);
      try {
        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: accessToken,
            RunId: runId,
          },
          signal,
        });
        if (!response.ok) {
          setErrorState(response.status, dispatch, runId, false);
          return;
        }
        const data = await response.json();
        if (data && data.value) {
          const subscriptionDtos: ProjectSubscription[] =
            mapDataToModels<ProjectSubscription>(data.value);
          setSubscribedProjects(subscriptionDtos);
          setTotalCount(data["@odata.count"] || 0);
        }
      } catch (error: any) {
        if (error?.name !== "AbortError") {
          setErrorState(500, dispatch, runId, true);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [
      accessToken,
      dispatch,
      skip,
      top,
      buildOrderBy,
      buildFilterStr,
      columnToODataPath,
      debugLoggingClause,
      setIsLoading,
      setTotalCount,
    ],
  );

  // Fetch when iTwin selection, pagination, sort, filter, or actionCount changes
  useEffect(() => {
    const abortController = new AbortController();

    if (selectedProject) {
      void fetchSubscribedProjects(selectedProject.id, abortController.signal);
    } else {
      setSubscribedProjects([]);
      setTotalCount(0);
    }

    return () => {
      abortController.abort();
    };
  }, [selectedProject, actionCount, fetchSubscribedProjects, setTotalCount]);

  function onFrollClose(refreshList?: boolean) {
    setFrollUrl(undefined);
    setSelectedPackage(null);
    if (refreshList) {
      setActionCount((prev) => prev + 1);
    }
  }

  const allHeadersHeight =
    mainMenuHeight + headerDivHeight + headerButtonsDivHeight;

  return (
    <div style={{ height: "100%" }}>
      <InformationPanelWrapper
        style={{ height: "100%", maxWidth: "100%", borderRadius: 0 }}
      >
        <div>
          {fRollUrl !== undefined && <div className="overlay" />}
          <div style={{ display: "flex", justifyContent: "center" }}>
            <div
              style={
                fRollUrl !== undefined
                  ? { pointerEvents: "none", opacity: "0.8" }
                  : {}
              }
            >
              <div className="becs-infopanelbody-footer-line">
                <div className="becs-all-margin" ref={headerDivRef}>
                  <div className="rowWrap">
                    <Text variant="title">{"Subscribed projects"}</Text>
                  </div>
                </div>
              </div>

              <div className="becs-ltr-margin">
                <div ref={headerButtonsDivRef}>
                  <div
                    className="horizontal becs-tb-margin becs-t-margin"
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <div
                        className="becs-input-label required"
                        style={{ fontWeight: 400 }}
                      >
                        Select iTwin project (showing {projects.length})
                      </div>
                    </div>

                    <ComboBox
                      emptyStateMessage={emptyContent}
                      options={appendLoadMoreOption(selectOptions, !!nextLink)}
                      onChange={(selected: ITwin | typeof LOAD_MORE_VALUE) => {
                        if (selected !== LOAD_MORE_VALUE) {
                          handleProjectSelect(selected);
                          setPageIndex(0);
                        }
                      }}
                      inputProps={{
                        placeholder: searchPlaceholder("Search iTwin project", 3, isProjectsLoading),
                        onChange: handleSearchChange,
                      }}
                      filterFunction={(options) => options}
                      value={selectedProject}
                      itemRenderer={paginatedItemRenderer<ITwin>({
                        onLoadMore: fetchNextProjects,
                        isLoading: isProjectsLoading,
                      })}
                    />
                  </div>
                </div>
                <div style={{ maxWidth: `calc(100vw - ${sideMenuWidth}px)` }}>
                  <Table
                    columns={columns}
                    emptyTableContent="No data."
                    data={tableData}
                    isLoading={isLoading}
                    style={{
                      height: `calc(100vh - ${allHeadersHeight}px)`,
                      width: `calc(100vw - ${sideMenuWidth}px)`,
                    }}
                    isSortable={true}
                    isResizable={false}
                    density="extra-condensed"
                    styleType="zebra-rows"
                    manualPagination
                    manualSortBy
                    manualFilters
                    paginatorRenderer={paginator}
                    pageSize={pageSize}
                    initialState={{
                      sortBy,
                      filters: tableFiltersState,
                    }}
                    isSelectable={true}
                    selectionMode="single"
                    subComponent={expandedSubComponent}
                    onSort={handleSort}
                    onFilter={handleFilter}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {fRollUrl !== undefined && (
          <InformationPanel
            resizable={true}
            isOpen={fRollUrl !== undefined}
            style={{
              maxWidth: "70%",
              minWidth: "40%",
              zIndex: 2,
            }}
            className="iui-infoPanel"
          >
            <InformationPanelHeader style={{ padding: 0 }}>
              <div className="becs-all-margin">
                <Text
                  variant="title"
                  style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                >
                  {fRollUrl === DEBUG_LOGGING_FROLL
                    ? `Debug logging for ${selectedPackage?.Project?.Name}`
                    : `Manage access to ${selectedPackage?.Project?.Name}`}
                </Text>
              </div>
            </InformationPanelHeader>
            <InformationPanelBody style={{ padding: 0 }}>
              <InformationPanelContent
                displayStyle="default"
                style={{ padding: 0, height: "100%" }}
              >
                {fRollUrl === DEBUG_LOGGING_FROLL ? (
                  <ProjectDebugLoggingForm
                    packageUniqueName={selectedPackage?.Project?.UniqueName}
                    iTwinId={selectedProject?.id}
                    closeFrollFunction={onFrollClose}
                  />
                ) : (
                  <ManageAccess
                    packageUniqueName={selectedPackage?.Project?.UniqueName}
                    iTwinId={selectedProject?.id}
                    closeFrollFunction={onFrollClose}
                  />
                )}
              </InformationPanelContent>
            </InformationPanelBody>
          </InformationPanel>
        )}
      </InformationPanelWrapper>
    </div>
  );
};

export default SubscribedProjectList;
